import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2, Plus, MessageSquare, Search, Pencil, Trash2, Eye, EyeOff, ChartBar,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";
import SurveyConversionSection from "@/pages/SurveyConversionSection";
import usePersistentState from "@/hooks/usePersistentState";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SurveyLibrary() {
  const nav = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = usePersistentState("surveys:search", "");
  const [conversionEnabled, setConversionEnabled] = useState(false);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", anonymous: false, allow_resubmit: false });
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchSurveys = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/surveys`, { withCredentials: true });
      setSurveys(data || []);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error("Surveys are not yet enabled");
      } else {
        toast.error("Failed to load surveys");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSurveys(); }, [fetchSurveys]);

  // Check the survey_conversion_enabled flag — folded into the library per Phase 7
  // wrap-up. Standalone /admin/survey-conversion nav was removed.
  useEffect(() => {
    axios.get(`${API}/admin/migration/parity/full`, { withCredentials: true })
      .then(({ data }) => setConversionEnabled(!!data?.flags?.survey_conversion_enabled))
      .catch(() => setConversionEnabled(false));
  }, []);

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/surveys`, form, { withCredentials: true });
      toast.success("Survey created");
      setShowCreate(false);
      setForm({ title: "", description: "", anonymous: false, allow_resubmit: false });
      // Jump straight into the builder
      nav(`/admin/surveys/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create survey");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/surveys/${deleteTarget.id}`, { withCredentials: true });
      toast.success("Survey deleted");
      setDeleteTarget(null);
      fetchSurveys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const togglePublish = async (s) => {
    try {
      await axios.put(
        `${API}/surveys/${s.id}`,
        { status: s.status === "published" ? "draft" : "published" },
        { withCredentials: true },
      );
      toast.success(s.status === "published" ? "Moved to draft" : "Published");
      fetchSurveys();
    } catch {
      toast.error("Status change failed");
    }
  };

  const filtered = search.trim()
    ? surveys.filter((s) => s.title.toLowerCase().includes(search.toLowerCase()))
    : surveys;

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return "—"; }
  };

  return (
    <div className="space-y-6" data-testid="survey-library">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold" style={{ color: "var(--admin-text)" }}>Survey Library</h1>
          <p className="text-sm font-body mt-1" style={{ color: "var(--admin-container-muted)" }}>
            {surveys.length} survey{surveys.length !== 1 ? "s" : ""} · Track-level perception surveys, no scoring.
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="gap-1.5 font-body text-xs h-9"
          style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
          data-testid="create-survey-btn"
        >
          <Plus className="h-3.5 w-3.5" /> New Survey
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: "var(--admin-container-muted)" }} />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search surveys..."
          className="pl-8 h-9 text-sm"
          style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
          data-testid="survey-search"
        />
      </div>

      {conversionEnabled && <SurveyConversionSection />}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--primary-color)" }} /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 rounded-xl border" style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}>
          <MessageSquare className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--admin-container-muted)" }} />
          <p className="text-sm font-body font-bold" style={{ color: "var(--admin-container-text)" }}>{search ? "No matching surveys" : "No surveys yet"}</p>
          <p className="text-xs font-body mt-1" style={{ color: "var(--admin-container-muted)" }}>
            {search ? "Try a different search." : "Create your first survey to capture learner feedback at the end of a track."}
          </p>
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}>
          <div className="hidden md:grid grid-cols-14 gap-3 px-4 py-2.5 text-[10px] uppercase tracking-wider font-body border-b"
            style={{ color: "var(--admin-container-muted)", borderColor: "var(--admin-container-border)", gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }}>
            <div className="col-span-4">Title</div>
            <div className="col-span-2">Items</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-3">Response rate</div>
            <div className="col-span-2">Last edited</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          <div className="divide-y" style={{ borderColor: "var(--admin-container-border)" }}>
            {filtered.map((s) => (
              <div key={s.id} className="grid gap-3 px-4 py-3 items-center hover:bg-white/[0.02] transition-colors" style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }} data-testid={`survey-row-${s.id}`}>
                <div className="col-span-4 flex items-center gap-2 min-w-0">
                  <MessageSquare className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "var(--primary-color)" }} />
                  <button
                    onClick={() => nav(`/admin/surveys/${s.id}`)}
                    className="text-sm font-body font-medium truncate text-left hover:underline"
                    style={{ color: "var(--admin-container-text)" }}
                    data-testid={`survey-open-${s.id}`}
                  >
                    {s.title}
                  </button>
                  {s.anonymous && (
                    <Badge variant="outline" className="text-[10px] font-body shrink-0 gap-1"
                      style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}>
                      Anonymous
                    </Badge>
                  )}
                </div>
                <div className="col-span-2 text-sm font-body" style={{ color: "var(--admin-container-text)" }}>
                  {s.item_count ?? 0}
                </div>
                <div className="col-span-2">
                  <Badge
                    className="text-[10px] font-body"
                    style={s.status === "published"
                      ? { backgroundColor: "color-mix(in srgb, #0d9488 20%, transparent)", color: "#0d9488", border: "1px solid #0d9488" }
                      : { backgroundColor: "var(--admin-bg)", color: "var(--admin-container-muted)", border: "1px solid var(--admin-container-border)" }
                    }
                  >
                    {s.status || "draft"}
                  </Badge>
                </div>
                <ResponseRateCell survey={s} />
                <div className="col-span-2 text-xs font-body" style={{ color: "var(--admin-container-muted)" }}>
                  {formatDate(s.updated_at || s.created_at)}
                </div>
                <div className="col-span-1 flex items-center justify-end gap-1">
                  <button
                    onClick={() => togglePublish(s)}
                    className="p-1.5 rounded hover:bg-white/[0.04]"
                    title={s.status === "published" ? "Unpublish" : "Publish"}
                    data-testid={`survey-toggle-publish-${s.id}`}
                  >
                    {s.status === "published"
                      ? <EyeOff className="h-3.5 w-3.5" style={{ color: "var(--admin-container-muted)" }} />
                      : <Eye className="h-3.5 w-3.5" style={{ color: "var(--primary-color)" }} />}
                  </button>
                  <button
                    onClick={() => nav(`/admin/surveys/${s.id}`)}
                    className="p-1.5 rounded hover:bg-white/[0.04]"
                    title="Edit"
                    data-testid={`survey-edit-${s.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" style={{ color: "var(--primary-color)" }} />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(s)}
                    className="p-1.5 rounded hover:bg-red-500/10"
                    title="Delete"
                    data-testid={`survey-delete-${s.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-red-400/70" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent
          className="max-w-lg"
          style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}
        >
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: "var(--admin-container-text)" }}>New Survey</DialogTitle>
            <DialogDescription className="font-body" style={{ color: "var(--admin-container-muted)" }}>
              Name your survey and choose anonymity settings. You'll add items in the next step.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-body mb-1.5 block" style={{ color: "var(--admin-container-muted)" }}>Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="e.g. End-of-track feedback"
                className="text-sm"
                style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
                data-testid="survey-title-input"
              />
            </div>
            <div>
              <Label className="text-xs font-body mb-1.5 block" style={{ color: "var(--admin-container-muted)" }}>Description (optional)</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="What is this survey measuring?"
                rows={2}
                className="text-sm resize-none"
                style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
                data-testid="survey-desc-input"
              />
            </div>
            <div className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ backgroundColor: "var(--admin-bg)", border: "1px solid var(--admin-container-border)" }}>
              <div>
                <Label className="text-xs font-body font-medium" style={{ color: "var(--admin-container-text)" }}>Anonymous responses</Label>
                <p className="text-[11px] font-body mt-0.5" style={{ color: "var(--admin-container-muted)" }}>Responses won't be linked to learner identity.</p>
              </div>
              <Switch
                checked={form.anonymous}
                onCheckedChange={(v) => setForm((f) => ({ ...f, anonymous: v }))}
                data-testid="survey-anonymous-switch"
              />
            </div>
            <div className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ backgroundColor: "var(--admin-bg)", border: "1px solid var(--admin-container-border)" }}>
              <div>
                <Label className="text-xs font-body font-medium" style={{ color: "var(--admin-container-text)" }}>Allow resubmission</Label>
                <p className="text-[11px] font-body mt-0.5" style={{ color: "var(--admin-container-muted)" }}>Let learners submit the survey multiple times.</p>
              </div>
              <Switch
                checked={form.allow_resubmit}
                onCheckedChange={(v) => setForm((f) => ({ ...f, allow_resubmit: v }))}
                data-testid="survey-resubmit-switch"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setShowCreate(false)} className="text-xs font-body"
                style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleCreate} disabled={saving} className="text-xs font-body gap-1.5"
                style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
                data-testid="survey-save-btn">
                {saving && <Loader2 className="h-3 w-3 animate-spin" />}Create & Build
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}>
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: "var(--admin-container-text)" }}>Delete Survey</DialogTitle>
            <DialogDescription className="font-body" style={{ color: "var(--admin-container-muted)" }}>
              Delete <strong style={{ color: "var(--admin-container-text)" }}>{deleteTarget?.title}</strong>? This also removes any track placements and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)} className="text-xs font-body"
              style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleDelete} disabled={deleting} className="text-xs font-body gap-1.5 bg-red-600 hover:bg-red-700 text-white"
              data-testid="survey-confirm-delete-btn">
              {deleting && <Loader2 className="h-3 w-3 animate-spin" />}Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResponseRateCell({ survey }) {
  if (survey.anonymous) {
    return (
      <div className="col-span-3 text-xs font-body" style={{ color: "var(--admin-container-muted)" }} data-testid={`survey-rate-${survey.id}`}>
        <span style={{ color: "var(--admin-container-text)" }}>{survey.anonymous_response_row_count || 0}</span>
        <span className="ml-1">rows · anonymous</span>
      </div>
    );
  }
  const rate = survey.response_rate;
  const respondents = survey.respondent_count ?? 0;
  const eligible = survey.eligible_count ?? 0;
  if (eligible === 0) {
    return (
      <div className="col-span-3 text-xs font-body" style={{ color: "var(--admin-container-muted)" }} data-testid={`survey-rate-${survey.id}`}>
        {survey.status === "published" ? "Not placed in a track" : "—"}
      </div>
    );
  }
  const tone =
    rate === null ? "muted" :
    rate >= 75 ? "good" :
    rate >= 40 ? "warn" :
    "bad";
  const color =
    tone === "good" ? "#0d9488" :
    tone === "warn" ? "#d97706" :
    tone === "bad" ? "#ef4444" :
    "var(--admin-container-muted)";
  return (
    <div className="col-span-3 flex items-center gap-2" data-testid={`survey-rate-${survey.id}`}>
      <div className="flex-1 h-1.5 rounded-full overflow-hidden max-w-[80px]" style={{ backgroundColor: "var(--admin-bg)" }}>
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${Math.min(100, rate ?? 0)}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-xs font-heading font-bold" style={{ color }}>
        {rate !== null ? `${rate}%` : "—"}
      </span>
      <span className="text-[10px] font-body" style={{ color: "var(--admin-container-muted)" }}>
        {respondents}/{eligible}
      </span>
    </div>
  );
}
